import { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { TableOfContentsItem } from '@/contexts/TableOfContentContext';

/**
 * Threshold in pixels to determine if scroll is at top or bottom
 */
const SCROLL_THRESHOLD = 8; // 8px to prevent false positives when scrolling

/**
 * Minimum height in pixels for an element to be considered "small"
 * Small elements get a bonus in visibility scoring to prevent them from being skipped
 */
const SMALL_ELEMENT_THRESHOLD = 192; // 8 * 24 to keep on a grid similar to tailwinf;

/**
 * Args for the useTableOfContentsHighlighting hook
 */
interface UseTableOfContentsHighlightingArgs {
  /** List of TOC items to track */
  items: TableOfContentsItem[];
  /** Reference to the scrollable viewport element */
  viewportRef: React.RefObject<HTMLDivElement> | null;
  /** Callback to set the active item */
  setActiveItem: (id: string) => void;
}

/**
 * Hook that handles highlighting for table of contents items based on viewport visibility
 *
 * This hook:
 * - Tracks which item is most visible in the viewport
 * - Handles special cases (at top, at bottom, most content visible)
 * - Supports programmatic navigation between items
 * - Uses multiple pass algorithm to accurately determine visibility
 * - Applies special handling for very small sections to prevent skipping
 *
 * @param args Configuration options for the hook
 * @returns Controls for the hook functionality
 */
export const useTableOfContentsHighlighting = ({
  items,
  viewportRef,
  setActiveItem,
}: UseTableOfContentsHighlightingArgs) => {
  const [mostVisibleItemId, setMostVisibleItemId] = useState<string | null>(
    null,
  );
  const [navigatingToItemId, setNavigatingToItemId] = useState<string | null>(
    null,
  );

  const debouncedResetActiveItem = useDebouncedCallback(() => {
    setNavigatingToItemId(null);
  }, 100);

  // Calculate which item is most visible in the viewport of the ScrollArea
  useEffect(() => {
    if (items.length === 0 || !viewportRef?.current) return;

    const scrollAreaContainer = viewportRef.current;

    /**
     * Calculates which TOC item is most visible in the viewport and updates the active item
     *
     * Algorithm:
     * 1. First pass: find top-most visible item
     * 2. Second pass: calculate visibility scores for all items
     * 3. Handle special cases (most content visible, at top, at bottom)
     * 4. Apply bonuses for small sections to prevent skipping
     */
    const calculateMostVisibleItem = () => {
      let maxVisibilityScore = 0;
      let mostVisibleId: string | null = null;
      let topMostId: string | null = null;

      // Detect scroll position relative to container
      const isScrollAtTop = scrollAreaContainer.scrollTop < SCROLL_THRESHOLD;
      const isScrollAtBottom =
        scrollAreaContainer.scrollHeight -
          scrollAreaContainer.scrollTop -
          scrollAreaContainer.clientHeight <
        SCROLL_THRESHOLD;

      // Check if most content is visible (scroll container is taller than content or close to it)
      const isMostContentVisible =
        scrollAreaContainer.clientHeight >=
        scrollAreaContainer.scrollHeight * 0.8;

      const containerRect = scrollAreaContainer.getBoundingClientRect();
      const scrollTop = scrollAreaContainer.scrollTop;
      const containerHeight = containerRect.height;

      // First pass: find the top-most visible item
      for (const item of items) {
        const element = document.getElementById(item.id);
        if (!element) continue;

        const rect = element.getBoundingClientRect();

        // Calculate element's position relative to the scroll container
        const elementTop =
          rect.top + window.scrollY - containerRect.top + scrollTop;
        const elementBottom = elementTop + rect.height;

        // If the element is visible (at least partially)
        if (
          elementBottom > scrollTop &&
          elementTop < scrollTop + containerHeight
        ) {
          // If this is the first visible item we've found, or it's higher than our current top-most
          if (
            topMostId === null ||
            elementTop <
              (document.getElementById(topMostId)?.getBoundingClientRect()
                .top || Infinity)
          ) {
            topMostId = item.id;
          }
        }
      }

      // Second pass: calculate visibility scores for all items
      for (const item of items) {
        const element = document.getElementById(item.id);
        if (!element) continue;

        const rect = element.getBoundingClientRect();

        // Calculate element's position relative to the scroll container
        const elementTop =
          rect.top + window.scrollY - containerRect.top + scrollTop;
        const elementBottom = elementTop + rect.height;
        const elementHeight = rect.height;

        // Calculate how much of the element is visible within the scroll container
        const visibleTop = Math.max(scrollTop, elementTop);
        const visibleBottom = Math.min(
          scrollTop + containerHeight,
          elementBottom,
        );
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        // Calculate visibility percentage & viewport occupancy
        const visibilityPercentage =
          elementHeight > 0 ? visibleHeight / elementHeight : 0;
        const viewportOccupancy = visibleHeight / containerHeight;

        // Special case for elements at the top of the viewport
        const isAtTopOfViewport = Math.abs(elementTop - scrollTop) < 50;
        const topPositionBonus = isAtTopOfViewport ? 0.2 : 0;

        // Special case for elements at the bottom of the viewport when scrolled to bottom
        const isAtBottomOfViewport =
          isScrollAtBottom &&
          Math.abs(scrollTop + containerHeight - elementBottom) < 50;
        const bottomPositionBonus = isAtBottomOfViewport ? 0.2 : 0;

        // Special case for very small sections that might otherwise be skipped
        const isSmallElement = elementHeight < SMALL_ELEMENT_THRESHOLD;
        const smallElementBonus = isSmallElement ? 0.25 : 0;

        // Special case for elements that are mostly in view (to prevent skipping)
        // This helps when an element is very small but fully visible
        const isHighlyVisible = visibilityPercentage > 0.8;
        const highVisibilityBonus = isHighlyVisible ? 0.15 : 0;

        // Calculate a visibility score that factors in:
        // 1. How much of the element is visible (percentage)
        // 2. How much of the viewport the element occupies
        // 3. Priority for being at the top of the viewport
        // 4. Priority for being at the bottom when scrolled to bottom
        // 5. Priority for small elements that might be skipped
        // 6. Priority for elements that are mostly in view
        const visibilityScore =
          visibilityPercentage * 0.45 +
          viewportOccupancy * 0.25 +
          topPositionBonus +
          bottomPositionBonus +
          smallElementBonus +
          highVisibilityBonus;

        if (navigatingToItemId && navigatingToItemId === item.id) {
          debouncedResetActiveItem();
        }

        // Track the previous visible item for potential small element fallback
        if (visibleHeight > 0) {
          if (mostVisibleId !== null) {
            topMostId = mostVisibleId;
          }

          // Accept this item if it has better visibility or if it's the first item we found
          if (visibilityScore > maxVisibilityScore || mostVisibleId === null) {
            maxVisibilityScore = visibilityScore;
            mostVisibleId = item.id;
          }
        }
      }

      // Apply special case priorities to override normal visibility scores
      if (isMostContentVisible && topMostId) {
        // If most content is visible on screen, prioritize the item closest to the top of the viewport
        mostVisibleId = topMostId;
      } else if (isScrollAtTop && topMostId) {
        // If we're at the top of the scroll and have a top-most item, prioritize it
        mostVisibleId = topMostId;
      } else if (isScrollAtBottom) {
        // If we're at the bottom of the scroll, find the last visible item
        let lastVisibleItem: string | null = null;
        let lastVisibleTop = -Infinity;

        for (const item of items) {
          const element = document.getElementById(item.id);
          if (!element) continue;

          const rect = element.getBoundingClientRect();
          const elementTop =
            rect.top +
            window.scrollY -
            containerRect.top +
            scrollAreaContainer.scrollTop;
          const elementBottom = elementTop + rect.height;
          const elementHeight = rect.height;

          // Apply a special check for small sections at the bottom
          const isSmallElement = elementHeight < SMALL_ELEMENT_THRESHOLD;

          // If this element is visible and is lower than our current last visible
          // For small elements, we lower the threshold to make them more likely to be selected
          if (
            elementBottom >
              (isSmallElement
                ? scrollAreaContainer.scrollTop - 20
                : scrollAreaContainer.scrollTop) &&
            elementTop < scrollAreaContainer.scrollTop + containerHeight &&
            elementTop > lastVisibleTop
          ) {
            lastVisibleTop = elementTop;
            lastVisibleItem = item.id;
          }
        }

        if (lastVisibleItem) {
          mostVisibleId = lastVisibleItem;
        }
      }

      // Only update if we have a new most visible item and we're not navigating programmatically
      if (
        mostVisibleId &&
        mostVisibleId !== mostVisibleItemId &&
        !navigatingToItemId
      ) {
        setMostVisibleItemId(mostVisibleId);
        setActiveItem(mostVisibleId);
      }
    };

    // Add event listeners and observers for different ways the content can change
    scrollAreaContainer.addEventListener('scroll', calculateMostVisibleItem, {
      passive: true,
    });

    calculateMostVisibleItem();

    const resizeObserver = new ResizeObserver(() => {
      calculateMostVisibleItem();
    });

    resizeObserver.observe(scrollAreaContainer);

    // Create mutation observer to detect content changes
    const mutationObserver = new MutationObserver(() => {
      calculateMostVisibleItem();
    });

    mutationObserver.observe(scrollAreaContainer, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });

    // Clean up event listeners and observers
    return () => {
      scrollAreaContainer.removeEventListener(
        'scroll',
        calculateMostVisibleItem,
      );
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [
    items,
    setActiveItem,
    mostVisibleItemId,
    viewportRef,
    navigatingToItemId,
    debouncedResetActiveItem,
  ]);

  // Select first item when items change and none is selected
  useEffect(() => {
    if (items.length > 0 && !mostVisibleItemId) {
      setActiveItem(items[0].id);
    }
  }, [items, mostVisibleItemId, setActiveItem]);

  return {
    /** ID of the item that is currently most visible in the viewport */
    mostVisibleItemId,
    /**
     * Set the ID of the item being navigated to programmatically
     * This prevents scroll calculations from overriding the active item during navigation
     */
    setNavigatingToItemId,
  };
};
